/**
 * Annual review correspondence — every email the review sent and every reply
 * it received, the real message downloadable (.eml), organized by department.
 *
 *   scope="campaign" + campaignId  — compliance: one campaign, all departments
 *   scope="mine"                   — a department user: their departments' mail
 *                                    and the messages addressed to them
 */
import React, { useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const KIND_LABEL = {
  launch: 'Launch email',
  delegate: 'Handed to a teammate',
  return: 'Returned to owner',
  department_archive: 'Department archive to compliance',
  launch_summary: 'Launch summary to compliance',
  reply: 'Reply',
};

function when(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}

function bytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function Message({ m, base, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const received = m.direction === 'received';
  return (
    <div className={`rounded-xl bg-white ring-1 ${received ? 'ring-emerald-200' : 'ring-slate-200'} p-3 ${depth ? 'ml-6 mt-2' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-slate-800 truncate">{m.subject || '(no subject)'}</p>
          <p className="text-[11.5px] text-slate-500 mt-0.5">
            <span className={`inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase mr-1.5 ${received ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{KIND_LABEL[m.kind] || m.kind}</span>
            {received ? `From ${m.counterpart_name || m.from_email}` : `To ${(m.to_emails || []).join(', ')}`}
            {m.cc_emails?.length ? ` · cc ${m.cc_emails.join(', ')}` : ''}
            {' · '}{when(m.sent_at)}
            {m.policy_title ? ` · ${m.policy_number ? `${m.policy_number}: ` : ''}${m.policy_title}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button type="button" onClick={() => setOpen(v => !v)} className="px-2.5 py-1 rounded-lg border border-slate-200 text-[11.5px] text-slate-600 hover:border-slate-300">{open ? 'Hide' : 'Read'}</button>
          {m.eml_available && (
            <a href={`${base}/mail/${m.id}/eml`} className="px-2.5 py-1 rounded-lg border border-slate-200 text-[11.5px] text-slate-600 hover:border-slate-300">Download email (.eml)</a>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <pre className="text-[12.5px] text-slate-700 whitespace-pre-wrap font-sans leading-relaxed max-h-80 overflow-y-auto">{m.body_text || '(no text body)'}</pre>
        </div>
      )}
      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
        <p className="text-[11.5px] text-slate-500 mt-2">
          Attachments:{' '}
          {m.attachments.map((a, i) => (
            <span key={i}>
              {i > 0 ? ' · ' : ''}
              {a.file_id
                ? <a href={`${base}/mail/${m.id}/files/${a.file_id}`} className="text-orange-700 hover:text-orange-800 font-medium">{a.name}</a>
                : a.name}
              {a.size_bytes ? ` (${bytes(a.size_bytes)})` : ''}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

export default function ReviewCorrespondence({ scope = 'campaign', campaignId = null }) {
  const [state, setState] = useState({ loading: true, mail: [], departments: [] });
  const [dept, setDept] = useState('all');
  const base = scope === 'mine' ? `${API}/api/ppm-review/my` : `${API}/api/ppm-review`;

  useEffect(() => {
    let alive = true;
    const url = scope === 'mine' ? `${API}/api/ppm-review/my/mail` : `${API}/api/ppm-review/campaigns/${campaignId}/mail`;
    if (scope !== 'mine' && !campaignId) return undefined;
    fetch(url, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive) setState({ loading: false, mail: d.mail || [], departments: d.departments || [], error: d.success ? null : (d.message || 'Could not load.') }); })
      .catch(() => { if (alive) setState({ loading: false, mail: [], departments: [], error: 'Could not load the correspondence.' }); });
    return () => { alive = false; };
  }, [scope, campaignId]);

  // Threads: a sent message and the replies that answer it, under each department.
  const threads = useMemo(() => {
    const byId = new Map(state.mail.map(m => [m.id, { ...m, replies: [] }]));
    const roots = [];
    for (const m of byId.values()) {
      if (m.in_reply_to_mail_id && byId.has(m.in_reply_to_mail_id)) byId.get(m.in_reply_to_mail_id).replies.push(m);
      else roots.push(m);
    }
    for (const r of roots) r.replies.sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
    return roots;
  }, [state.mail]);

  const deptList = useMemo(() => {
    const names = new Map(state.departments.map(d => [String(d.id), d.name]));
    // A department user's list has no department directory; derive names from the mail itself.
    for (const m of state.mail) for (const id of (m.department_ids || [])) if (!names.has(String(id))) names.set(String(id), null);
    return [...names.entries()].map(([id, name]) => ({ id, name: name || 'Department' })).sort((a, b) => a.name.localeCompare(b.name));
  }, [state.departments, state.mail]);

  const visible = threads.filter(t => {
    if (dept === 'all') return true;
    if (dept === 'none') return !(t.department_ids || []).length;
    return (t.department_ids || []).map(String).includes(dept);
  });

  const countFor = (id) => threads.filter(t => (id === 'none' ? !(t.department_ids || []).length : (t.department_ids || []).map(String).includes(id))).length;

  if (state.loading) return <p className="text-[13px] text-slate-500 py-4">Loading the correspondence…</p>;
  if (state.error) return <p className="text-[13px] text-amber-700 py-4">{state.error}</p>;
  if (!state.mail.length) return <p className="text-[13px] text-slate-500 py-4">No emails on record yet.</p>;

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <button type="button" onClick={() => setDept('all')} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${dept === 'all' ? 'bg-slate-800 text-white' : 'bg-white ring-1 ring-slate-200 text-slate-600 hover:ring-slate-300'}`}>All · {threads.length}</button>
        {deptList.map(d => (
          <button key={d.id} type="button" onClick={() => setDept(d.id)} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${dept === d.id ? 'bg-slate-800 text-white' : 'bg-white ring-1 ring-slate-200 text-slate-600 hover:ring-slate-300'}`}>{d.name} · {countFor(d.id)}</button>
        ))}
        {threads.some(t => !(t.department_ids || []).length) && (
          <button type="button" onClick={() => setDept('none')} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${dept === 'none' ? 'bg-slate-800 text-white' : 'bg-white ring-1 ring-slate-200 text-slate-600 hover:ring-slate-300'}`}>Compliance only · {countFor('none')}</button>
        )}
      </div>
      <div className="space-y-2">
        {visible.map(t => (
          <div key={t.id}>
            <Message m={t} base={base} />
            {t.replies.map(r => <Message key={r.id} m={r} base={base} depth={1} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
